import { MenuItem } from '@material-ui/core';
import {
  useCreateCompanyPersonalDetail,
  useGetCompanyPersonalDetail,
  useSetRepairersActivity,
  useUpdateCompanyPersonalDetail,
} from 'app/core/api/company/api-company';
import BusinessRoutePath from 'app/business-app/BusinessRoutePath';
import {
  currencyCodes,
  mapCurrencyCodeToSymbol,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import { RepairPersonalDetail } from 'app/core/api/repairer/api-repairer.types';
import FormDateInput from 'app/shared/components/form-input/FormDateInput';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import {
  useEffectOnce,
  useEffectOnChanges,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useObservableEagerState } from 'observable-hooks';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import moment from 'moment';
import MultiPartInputBox from 'app/shared/frames/MultiPartInputBox';
import TextInput from 'app/shared/components/form-input/TextInput';
import FormSelectInput from 'app/shared/components/form-input/FormSelectInput';
import { PageDetails } from 'app/core/api/company/api-company.types';
// import { useMovePage } from '../create-business-profile/business-profile-utils';
import BottomPageAndSubmitActions from '../create-business-profile/input-pages/components/BottomActions';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

export default function UpdatePersonalDetail(): JSX.Element {
  useSetPageTitle('Personal Information');
  const history = useHistory();

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Personal Information',
    };
    repairActivity(pageDetails).subscribe();
  });
  const {
    request$: createPersonalDetail$,
    error: createError,
    sending: creatingPersonalDetail,
  } = useCreateCompanyPersonalDetail();

  const {
    request$: updatePersonalDetail$,
    error: updateError,
    sending: updatingPersonalDetail,
  } = useUpdateCompanyPersonalDetail();

  const {
    resource: personalDetails,
    loading,
    error: loadingError,
  } = useGetCompanyPersonalDetail({ fresh: true });

  // const { nextPage } = useMovePage();
  const { showSnack } = useShowInfo();

  const editable$ = useMemo(() => new BehaviorSubject(false), []);
  const editable = useObservableEagerState(editable$);
  useEffectOnChanges(() => {
    if (personalDetails) {
      editable$.next(false);
    } else {
      editable$.next(true);
    }
  }, [editable$, personalDetails, loadingError]);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, setError, inputProps, formState } =
    useInputForm<RepairPersonalDetail>({
      mode: 'onTouched',
      defaultValues:
        personalDetails && personalDetails?.length > 0
          ? personalDetails[0]
          : {},
      apiError: createError ?? updateError,
    });
  return (
    <>
      <Form
        flexStretch
        onSubmit={handleSubmit(details => {
          if (_.isEmpty(formState.touched)) {
            history.goBack();
            return;
          }
          // eslint-disable-next-line no-param-reassign
          details.companyacctype = 'individual';
          // eslint-disable-next-line no-param-reassign
          details.dob = moment(details.dob).format('YYYY-MM-DD');
          // eslint-disable-next-line no-param-reassign
          details.accountholdername = 'test';
          if (personalDetails && personalDetails?.length > 0) {
            return updatePersonalDetail$(details).pipe(
              tap(() => {
                showSnack({
                  message: 'Profile Updated!',
                  alertType: 'success',
                });
                history.push(`${BusinessRoutePath.PROFILE}`);
              })
            );
          } else {
            return createPersonalDetail$(details).pipe(
              tap(() => {
                showSnack({
                  message: 'Profile Created!',
                  alertType: 'success',
                });
                history.push(`${BusinessRoutePath.PROFILE}`);
              })
            );
          }
        })}
      >
        <FormTextInput
          controlName="firstName"
          placeholder="First Name"
          autoComplete="firstName"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="lastName"
          placeholder="Last Name"
          autoComplete="lastName"
          disabled={!editable}
          {...inputProps}
        />
        <FormDateInput
          classicPlaceholder
          placeholder="Date of Birth"
          controlName="dob"
          {...{ setError }}
          disabled={!editable}
          {...inputProps}
          format="yyyy-MM-dd"
        />
        <MultiPartInputBox variant="flush" {...inputProps}>
          <TextInput value="Currency" />
          <FormSelectInput
            classicPlaceholder
            controlName="currency"
            disabled={!editable}
            {...inputProps}
            defaultValue="GBP"
          >
            {Object.keys(currencyCodes).map(code => (
              <MenuItem value={code} key={code}>
                {mapCurrencyCodeToSymbol.get(code)}
              </MenuItem>
            ))}
          </FormSelectInput>
        </MultiPartInputBox>
        <FormTextInput
          controlName="phoneNumber"
          placeholder="Phone Number"
          required={false}
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="website"
          placeholder="Website"
          required={false}
          disabled={!editable}
          {...inputProps}
        />
        <Body tiny>Asterisks (*) indicate required fields</Body>
        <ErrorText message={createError} />
        {personalDetails && personalDetails?.length > 0 && (
          <BottomEditActions
            sending={creatingPersonalDetail || updatingPersonalDetail}
            {...{ editable$, loading }}
          />
        )}
        {(!personalDetails ||
          (personalDetails && personalDetails?.length === 0)) && (
          <BottomPageAndSubmitActions
            onBack={() => {
              history.goBack();
            }}
            {...{ creatingPersonalDetail }}
          />
        )}
      </Form>
    </>
  );
}
